import React, { useEffect, useRef, useState } from "react";
import type { components } from "../../../../types/api";
import type { ApiClient } from "../../../../types/UserTypes";
import SearchableSelect from "../../../../components/SearchableSelect";
import { asRecord, type FormattedInvoicePayload, type ReportExecuteResponseV3 } from "../../../formattedInvoice/formattedInvoiceData";
import { buildInvoiceExecutionFilters, INVOICE_FIELDS, INVOICE_SOURCES, invoiceDatasetDescriptors, mapNativeInvoiceExecution, nativeInvoiceSourceError, setInvoiceDatasetSource, type InvoiceFilterDraft, type InvoiceSourceId, type NativeExecutionRequest, type InvoiceExecutionFilters } from "../../nativeBusinessExecution";
import styles from "./ReportDesignerPage.module.css";
import { isReportAccessDenied, reportFailureMessage } from "../../reportAccess";

type Client = components["schemas"]["ClientLookupModel"];
type InvoiceOption = { id: number; label: string };
const invoiceSourceIds: InvoiceSourceId[] = ["invoiceHeader", "invoiceLines"];

export function NativeInvoiceSourceEditor({ data, reportType, onChange }: {
  data: unknown;
  reportType?: string;
  onChange: (data: Record<string, unknown>) => void;
}) {
  let descriptors: Record<string, unknown>[] = [];
  let configurationError = "";
  try {
    descriptors = invoiceDatasetDescriptors(data);
  } catch (error) {
    configurationError = error instanceof Error ? error.message : String(error);
  }
  const [editError, setEditError] = useState("");
  function changeSource(id: InvoiceSourceId, source: string) {
    try {
      onChange(setInvoiceDatasetSource(data, id, source));
      setEditError("");
    } catch (error) {
      setEditError(error instanceof Error ? error.message : String(error));
    }
  }
  return (
    <details className={styles["designer-tree-node"]} open>
      <summary>Execution sources</summary>
      <p className={styles["designer-empty-note"]}>Dataset IDs remain binding identities. Sources select authorized backend queries; changes require Save.</p>
      {reportType !== "invoice" && <p className={styles["designer-empty-note"]}>These mappings are for grouped invoices with reportType "invoice". Other bundles can be configured in report JSON.</p>}
      {invoiceSourceIds.map((id) => {
        const current = String(descriptors.find((descriptor) => descriptor.id === id)?.source || "");
        return (
          <label className={styles["designer-menu-field"]} key={id} data-testid={`designer-execution-source-${id}`}>
            <span className={styles["designer-label"]}>{id} ({INVOICE_SOURCES[id].kind})</span>
            <select aria-label={`Execution source for ${id}`} className={styles["designer-input"]} disabled={Boolean(configurationError) || reportType !== "invoice"} value={current} onChange={(event) => changeSource(id, event.target.value)}>
              <option value="">Not configured</option>
              {current && current !== INVOICE_SOURCES[id].source && <option value={current}>{current} (review mapping)</option>}
              <option value={INVOICE_SOURCES[id].source}>{INVOICE_SOURCES[id].source}</option>
            </select>
          </label>
        );
      })}
      {(configurationError || editError) && <p className={styles["designer-error"]} role="alert">{configurationError || editError}</p>}
      <p className={styles["designer-empty-note"]}>Entry amount includes line expenses. Quantity x rate is labor only. invoiceTotals.amountDue includes invoice-level adjustments.</p>
      <details>
        <summary>Grouped invoice field meanings</summary>
        {Object.entries(INVOICE_FIELDS).map(([datasetId, fields]) => (
          <dl key={datasetId} data-testid={`designer-field-meanings-${datasetId}`} className={styles["designer-field-meanings"]}>
            {fields.map((field) => <div key={field.path}><dt>{datasetId}.{field.path} ({field.type})</dt><dd>{field.description}</dd></div>)}
          </dl>
        ))}
      </details>
    </details>
  );
}

export default function NativeBusinessDataControls({ api, definitionId, definitionVersion, data, reportType, requireInvoice, ready, onResult, onAccessDenied }: {
  api: ApiClient | null;
  definitionId: string;
  definitionVersion?: number;
  data: unknown;
  reportType?: string;
  requireInvoice: boolean;
  ready: boolean;
  onResult: (payload: FormattedInvoicePayload | null, filters?: InvoiceExecutionFilters) => void;
  onAccessDenied?: (error: unknown) => void;
}) {
  const [clients, setClients] = useState<Client[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientsReload, setClientsReload] = useState(0);
  const [draft, setDraft] = useState<InvoiceFilterDraft>({ clientId: null, startDate: "", endDate: "", invoiceId: "" });
  const [invoices, setInvoices] = useState<InvoiceOption[]>([]);
  const [invoiceSearchComplete, setInvoiceSearchComplete] = useState(false);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState("");
  const [accessDenied, setAccessDenied] = useState(false);
  const [resultNotice, setResultNotice] = useState("");
  const requestSequence = useRef(0);
  const invoiceSequence = useRef(0);
  const sourceError = nativeInvoiceSourceError(data, reportType);

  function handleFailure(failure: unknown) {
    if (isReportAccessDenied(failure)) {
      setAccessDenied(true);
      requestSequence.current += 1;
      invoiceSequence.current += 1;
      setClients([]);
      setInvoices([]);
      setExecuting(false);
      setInvoicesLoading(false);
      onResult(null);
      onAccessDenied?.(failure);
    }
    setError(reportFailureMessage(failure, "The report operation failed."));
  }
  const failureHandler = useRef(handleFailure);
  failureHandler.current = handleFailure;

  useEffect(() => {
    let disposed = false;
    if (!api) return;
    setClientsLoading(true);
    api.get<Client[]>("clientinfo/lookup").then((rows) => {
      if (!Array.isArray(rows)) throw new Error("Client lookup did not return a client list.");
      if (!disposed) setClients([...rows].sort((a, b) => String(a.businessName || "").localeCompare(String(b.businessName || ""))));
    }).catch((failure: unknown) => {
      if (!disposed) {
        if (isReportAccessDenied(failure)) failureHandler.current(failure);
        else setError(`Could not load authorized clients: ${errorMessage(failure)}`);
      }
    }).finally(() => {
      if (!disposed) setClientsLoading(false);
    });
    return () => { disposed = true; };
  }, [api, clientsReload]);

  useEffect(() => () => {
    requestSequence.current += 1;
    invoiceSequence.current += 1;
  }, []);

  function changeFilters(change: Partial<InvoiceFilterDraft>, clearInvoices = true) {
    requestSequence.current += 1;
    setExecuting(false);
    if (clearInvoices) {
      invoiceSequence.current += 1;
      setInvoicesLoading(false);
      setInvoices([]);
      setInvoiceSearchComplete(false);
    }
    setDraft((current) => ({ ...current, ...(clearInvoices ? { invoiceId: "" } : {}), ...change }));
    setError("");
    setResultNotice("");
    onResult(null);
  }

  async function findInvoices() {
    if (!api) return;
    const sequence = ++invoiceSequence.current;
    try {
      const filters = buildInvoiceExecutionFilters({ ...draft, invoiceId: "" }, false);
      setInvoicesLoading(true);
      setError("");
      const response = await api.get<unknown>(`ReportsV2/GetInvoices/${filters.clientId}/${filters.startDate}/${filters.endDate}`);
      if (!Array.isArray(response)) throw new Error("Invoice lookup did not return a list.");
      const options = response.flatMap((item: unknown): InvoiceOption[] => {
        const row = asRecord(item);
        const id = Number(row.invoiceId ?? row.outstandingInvoiceId ?? row.id);
        if (!Number.isInteger(id) || id <= 0) throw new Error("Invoice lookup returned an invalid invoice identity.");
        if (typeof row.startDate !== "string" || typeof row.endDate !== "string") throw new Error("Invoice lookup did not include its calendar period. Select the invoice's exact start and end dates before executing.");
        if (row.startDate.slice(0, 10) !== filters.startDate || row.endDate.slice(0, 10) !== filters.endDate) return [];
        return [{ id, label: String(row.invoiceName || row.reportName || `Invoice ${id}`) }];
      });
      if (sequence !== invoiceSequence.current) return;
      setInvoices(options);
      setInvoiceSearchComplete(true);
    } catch (failure) {
      if (sequence === invoiceSequence.current) handleFailure(failure);
    } finally {
      if (sequence === invoiceSequence.current) setInvoicesLoading(false);
    }
  }

  async function execute() {
    if (!api || !ready || sourceError) return;
    const sequence = ++requestSequence.current;
    onResult(null);
    setResultNotice("");
    setError("");
    try {
      const filters = buildInvoiceExecutionFilters(draft, requireInvoice);
      setExecuting(true);
      const request: NativeExecutionRequest = { definitionId, output: "preview", filters };
      const response = await api.post<ReportExecuteResponseV3>("ReportsV3/execute", request);
      const payload = mapNativeInvoiceExecution(response, definitionId, definitionVersion);
      if (sequence !== requestSequence.current) return;
      onResult(payload, filters);
      const rows = payload.datasets.invoiceLines as unknown[];
      const warnings = Array.isArray(payload.metadata.assetWarnings) ? payload.metadata.assetWarnings.join(", ") : "";
      setResultNotice(`${rows.length} authorized invoice row${rows.length === 1 ? "" : "s"}. ${payload.definitionId} v${payload.definitionVersion}. ${rows.length ? "Preview and PDF use this same execution result." : "No business records for these filters; no sample records were substituted."}${warnings ? ` Logo: ${warnings}.` : ""}`);
    } catch (failure) {
      if (sequence === requestSequence.current) handleFailure(failure);
    } finally {
      if (sequence === requestSequence.current) setExecuting(false);
    }
  }

  if (accessDenied) return <section aria-label="Business Data execution" className={styles["designer-business-card"]}>
    <p className={styles["designer-error"]} role="alert">{error} Reload this report to refresh access. No sample data has been substituted.</p>
  </section>;

  return (
    <section aria-label="Business Data execution" className={styles["designer-business-card"]}>
      <strong>Real Business Data Preview</strong>
      <p className={styles["designer-empty-note"]}>Executes the saved definition using authorized tenant records. Unbilled time is used unless you select a generated invoice.</p>
      {!ready && <p role="status">Save or reload this native report before executing. Unsaved edits are never substituted for the saved definition.</p>}
      {sourceError && <p className={styles["designer-error"]} role="alert">{sourceError}</p>}
      <div className={styles["designer-business-filters"]}>
        <div className={styles["designer-business-field"]}>
          <label className={styles["designer-label"]} htmlFor="designerBusinessClient">Client</label>
          <SearchableSelect id="designerBusinessClient" className={styles["designer-input"]} value={draft.clientId} options={clients.flatMap((client) => typeof client.clientId === "number" && client.clientId > 0 ? [{ value: client.clientId, label: client.businessName || `Client ${client.clientId}` }] : [])} onChange={(clientId) => changeFilters({ clientId })} disabled={!api || clientsLoading} placeholder={clientsLoading ? "Loading clients..." : "Select a client..."} />
        </div>
        <label className={styles["designer-business-field"]} htmlFor="designerBusinessStart">
          <span className={styles["designer-label"]}>Start date</span>
          <input id="designerBusinessStart" className={styles["designer-input"]} type="date" value={draft.startDate} onChange={(event) => changeFilters({ startDate: event.target.value })} />
        </label>
        <label className={styles["designer-business-field"]} htmlFor="designerBusinessEnd">
          <span className={styles["designer-label"]}>End date</span>
          <input id="designerBusinessEnd" className={styles["designer-input"]} type="date" value={draft.endDate} onChange={(event) => changeFilters({ endDate: event.target.value })} />
        </label>
        <button className={styles["designer-tool-btn"]} disabled={!api || invoicesLoading} onClick={findInvoices} type="button">{invoicesLoading ? "Finding invoices..." : "Find invoices"}</button>
        <label className={styles["designer-business-field"]} htmlFor="designerBusinessInvoice">
          <span className={styles["designer-label"]}>Invoice {requireInvoice ? "(required)" : "(optional)"}</span>
          <select id="designerBusinessInvoice" className={styles["designer-input"]} value={draft.invoiceId} onChange={(event) => changeFilters({ invoiceId: event.target.value }, false)}>
            <option value="">{requireInvoice ? "Select a generated invoice..." : "Unbilled preview"}</option>
            {invoices.map((invoice) => <option key={invoice.id} value={invoice.id} data-testid={`designer-business-invoice-${invoice.id}`}>{invoice.label}</option>)}
          </select>
        </label>
        <button className={`${styles["designer-tool-btn"]} ${styles["designer-tool-btn-primary"]}`} disabled={!api || !ready || Boolean(sourceError) || executing} onClick={execute} type="button">{executing ? "Executing..." : "Execute Business Data"}</button>
      </div>
      {invoiceSearchComplete && !invoices.length && <p className={styles["designer-empty-note"]}>No generated invoices for this client and exact period.</p>}
      {error && <p className={styles["designer-error"]} role="alert">{error} No sample data has been substituted.</p>}
      {error.startsWith("Could not load authorized clients:") && <button className={styles["designer-tool-btn"]} onClick={() => { setError(""); setClientsReload((value) => value + 1); }} type="button">Retry client lookup</button>}
      {resultNotice && <p role="status" data-testid="designer-business-execution-result">{resultNotice}</p>}
    </section>
  );
}

function errorMessage(error: unknown): string {
  const record = asRecord(error);
  return `${typeof record.code === "string" ? `${record.code}: ` : ""}${error instanceof Error ? error.message : String(error)}`;
}
